import { Injectable, inject } from '@angular/core';
import { ApiClientService } from './api-client.service';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { Damage, Smartphoto } from '@bdeo-motor/smartphoto-entities';
import { BdeoToastService } from '@bdeo/webcomponents';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { AuthService } from './auth.service';
import { IDamage } from '@bdeo-platform/private-api';

type PushImagePayload = {
  smartphoto: Smartphoto;
  key: string;
  file: File;
  damage: Damage;
};

@Injectable({
  providedIn: 'root'
})
export class DamageImagesService {
  protected apiClientService = inject(ApiClientService);
  protected i18NextService = inject(I18NEXT_SERVICE);
  protected http = inject(HttpClient);
  protected authService = inject(AuthService);

  public static addImagesToDamage(damage: Damage, imagesUuids: string[]): void {
    if (imagesUuids.length) {
      const [imageUuid, ...rest] = imagesUuids;
      if (!damage.image_info.image_uuid) {
        damage.image_info.image_uuid = imageUuid;
      } else {
        rest.unshift(imageUuid);
      }

      const { mainDetection } = damage.export();

      rest.forEach((uuid) => {
        const copy: IDamage = JSON.parse(JSON.stringify(mainDetection));
        copy.damages[0].uuid = crypto.randomUUID();
        copy.image_info = {
          image_uuid: uuid
        };
        damage.others.push(new Damage(copy));
      });
    }
  }

  public async uploadDamageImages(smartphoto: Smartphoto, damage: Damage, images: File[]): Promise<string[]> {
    const results = await Promise.allSettled(images.map((image) => this.uploadImageToFileSystem(smartphoto, image)));

    if (results.some((result) => result.status === 'rejected')) {
      let partName = damage?.description ?? '';

      if (this.i18NextService.exists(`smartphoto:parts.${partName}`)) {
        partName = this.i18NextService.t(`smartphoto:parts.${partName}`);
      }

      const translated = this.i18NextService.t('smartphoto:some_images_upload_error_ocurred', {
        part: partName
      });

      BdeoToastService.warning(translated);
    }

    const uploadedImages = results.map((result) => (result.status === 'fulfilled' ? result.value : null)).filter(Boolean) as {
      file: File;
      uuid: string;
    }[];

    const ids = [];
    let index = 1;

    for await (const img of uploadedImages) {
      ids.push(
        await this.pushImageToSmartphotoImages(
          {
            smartphoto,
            key: img.uuid,
            file: img.file,
            damage
          },
          index
        )
      );
      index++;
    }

    return ids.filter((id): id is string => id !== undefined);
  }

  private async uploadImageToFileSystem(smartphoto: Smartphoto, image: File): Promise<{ file: File; uuid: string }> {
    const baseUrl = this.apiClientService.getBaseUrl();

    let extension = image.name.split('.').pop()?.toLowerCase() ?? '';

    if (extension === 'jpeg') {
      extension = 'jpg';
    }

    const { upload_url: uploadUrl, file_key: uuid } = await lastValueFrom(
      this.http.post<{ upload_url: string; file_key: string }>(
        `${baseUrl}/files-system/company/${smartphoto.masterCompanyId}/selfadjusts/${smartphoto.id}/media/upload`,
        {
          extension
        },
        {
          headers: {
            access_token: this.authService.getAccessToken()
          }
        }
      )
    );

    await lastValueFrom(
      this.http.put(uploadUrl, image, {
        headers: {
          'Content-Type': image.type
        }
      })
    );

    return { file: image, uuid };
  }

  private async pushImageToSmartphotoImages(payload: PushImagePayload, index: number): Promise<string | undefined> {
    try {
      let extension = payload.file.name.split('.').pop()?.toLowerCase() ?? '';

      if (extension === 'jpeg') {
        extension = 'jpg';
      }

      const { name, surname } = this.authService.getCurrentUser() ?? {};
      const hasName = name || surname;
      const fullName = `${name} ${surname}`.trim();

      const response = await this.apiClientService.getApiClient().motor.smartphoto.pushImage({
        id: payload.smartphoto.id,
        type: 'damage',
        caption: `${payload.damage.description} (${index})`,
        ext: extension,
        mediaId: payload.key,
        uploadedBy: hasName ? fullName : 'Bdeo agent'
      });

      return response.data.entity?.image_uuid;
    } catch (error) {
      console.error('Error pushing image to smartphoto:', error);
      BdeoToastService.error(this.i18NextService.t('smartphoto:error_pushing_image'));
    }
    return undefined;
  }
}
